<template>
  <div class="app-container receivable-ledger-page">
    <el-card shadow="never" class="query-card">
      <div class="page-heading">
        <div>
          <div class="page-title">应收台账</div>
          <div class="page-desc">
            一行一份合同，集中查看合同金额、累计回款、累计开票和当前未回款；统计分析已拆分到独立财务菜单。
          </div>
        </div>
        <el-button
          v-hasPermi="['crm:finance:receivableLedger:export']"
          type="primary"
          plain
          icon="el-icon-download"
          :loading="exporting"
          @click="handleExport"
        >导出全部台账及统计</el-button>
      </div>
      <el-form :inline="true" :model="queryParams" class="query-form">
        <el-form-item label="统计年份">
          <el-date-picker
            v-model="reportYear"
            type="year"
            value-format="yyyy"
            :clearable="false"
            style="width: 140px"
          />
        </el-form-item>
        <el-form-item label="签约年份">
          <el-select v-model="queryParams.contractYear" clearable placeholder="全部年份" style="width: 140px">
            <el-option
              v-for="year in dashboard.contractYearOptions"
              :key="year"
              :label="`${year}年`"
              :value="year"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="部门">
          <el-select v-model="queryParams.departmentName" clearable placeholder="全部部门" style="width: 170px">
            <el-option
              v-for="department in dashboard.departmentOptions"
              :key="department"
              :label="department"
              :value="department"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="产品大类">
          <el-select v-model="queryParams.productCategory" clearable placeholder="全部大类" style="width: 170px">
            <el-option
              v-for="category in dashboard.productCategoryOptions"
              :key="category"
              :label="category"
              :value="category"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="关键词">
          <el-input
            v-model.trim="queryParams.keyword"
            clearable
            placeholder="合同编号/名称/客户"
            style="width: 220px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-alert
      v-if="dashboard.summary.fallbackCategoryContractCount || dashboard.summary.mixedCategoryContractCount"
      type="warning"
      :closable="false"
      show-icon
      class="quality-alert"
    >
      <template slot="title">
        产品归类提示：{{ dashboard.summary.mixedCategoryContractCount || 0 }} 个跨大类合同按构成金额占比拆分；
        {{ dashboard.summary.fallbackCategoryContractCount || 0 }} 个历史合同无构成，暂归“工程及服务等”。
      </template>
    </el-alert>

    <el-card shadow="never" class="content-card">
          <div class="table-tip">表格可横向滚动查看 {{ reportYear }} 年1—12月非承兑回款、银票、回款和正常业务开票；历史导入开票仅计入累计开票。</div>
          <el-table :data="pagedLedgerRows" border stripe v-loading="loading" height="620">
            <el-table-column prop="contractYear" label="签约年份" width="92" fixed="left"/>
            <el-table-column prop="contractMonth" label="签约月份" width="88" fixed="left"/>
            <el-table-column prop="contractNum" label="合同编号" min-width="145" fixed="left" show-overflow-tooltip/>
            <el-table-column prop="archiveNo" label="档案序号" width="110"/>
            <el-table-column prop="archivedFlag" label="是否存档" width="90" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.archivedFlag === 1 ? 'success' : 'info'" size="mini">
                  {{ scope.row.archivedFlag === 1 ? '已存档' : '未存档' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="contractStatusName" label="状态" width="95"/>
            <el-table-column prop="reportYearFundUserName" :label="`${reportYear}年回款人`" width="125" show-overflow-tooltip/>
            <el-table-column prop="reportYearFundDepartmentName" :label="`${reportYear}年回款部门`" min-width="135" show-overflow-tooltip/>
            <el-table-column prop="assignUserName" label="签约人" width="100"/>
            <el-table-column prop="departmentName" label="签约部门" min-width="125"/>
            <el-table-column prop="customerName" label="客户名称" min-width="180" show-overflow-tooltip/>
            <el-table-column prop="productCategory" label="产品大类" min-width="155" show-overflow-tooltip/>
            <el-table-column prop="productType" label="产品构成" min-width="150" show-overflow-tooltip/>
            <el-table-column prop="productModel" label="膜片型号" min-width="125" show-overflow-tooltip/>
            <el-table-column prop="membraneArea" label="膜面积" width="110" align="right">
              <template slot-scope="scope">{{ amount(scope.row.membraneArea) }}</template>
            </el-table-column>
            <el-table-column prop="specificationQuantity" label="合同规格及数量" min-width="180" show-overflow-tooltip/>
            <el-table-column prop="invoiceAmount" label="累计开票" width="125" align="right">
              <template slot-scope="scope">{{ amount(scope.row.invoiceAmount) }}</template>
            </el-table-column>
            <el-table-column prop="contractAmount" label="签约总额" width="125" align="right">
              <template slot-scope="scope">{{ amount(scope.row.contractAmount) }}</template>
            </el-table-column>
            <el-table-column prop="receivedRate" label="回款比例" width="105" align="right">
              <template slot-scope="scope">{{ amount(scope.row.receivedRate) }}%</template>
            </el-table-column>
            <el-table-column prop="receivedAmount" label="累计回款" width="125" align="right">
              <template slot-scope="scope">{{ amount(scope.row.receivedAmount) }}</template>
            </el-table-column>
            <el-table-column prop="outstandingAmount" label="未回款" width="125" align="right">
              <template slot-scope="scope">{{ amount(scope.row.outstandingAmount) }}</template>
            </el-table-column>
            <el-table-column
              v-for="(month, monthIndex) in months"
              :key="month.value"
              :label="month.label"
              align="center"
            >
              <el-table-column label="非承兑" width="115" align="right">
                <template slot-scope="scope">{{ amount(scope.row.cashMonthly[monthIndex]) }}</template>
              </el-table-column>
              <el-table-column label="银票" width="115" align="right">
                <template slot-scope="scope">{{ amount(scope.row.acceptanceMonthly[monthIndex]) }}</template>
              </el-table-column>
              <el-table-column label="回款" width="115" align="right">
                <template slot-scope="scope">{{ amount(scope.row.fundMonthly[monthIndex]) }}</template>
              </el-table-column>
              <el-table-column label="正常开票" width="115" align="right">
                <template slot-scope="scope">{{ amount(scope.row.invoiceMonthly[monthIndex]) }}</template>
              </el-table-column>
            </el-table-column>
            <el-table-column
              v-hasPermi="['crm:finance:receivableLedger:edit']"
              label="维护"
              width="86"
              fixed="right"
              align="center"
            >
              <template slot-scope="scope">
                <el-button type="text" icon="el-icon-edit" @click="openMetaDialog(scope.row)">维护</el-button>
              </template>
            </el-table-column>
          </el-table>
          <pagination
            v-show="dashboard.ledgerTotal > 0"
            :total="dashboard.ledgerTotal"
            :page.sync="pageNum"
            :limit.sync="pageSize"
            @pagination="queryData"
          />
    </el-card>

    <el-dialog title="维护财务台账信息" :visible.sync="metaDialogVisible" width="520px">
      <el-form ref="metaForm" :model="metaForm" :rules="metaRules" label-width="110px">
        <el-form-item label="合同编号">
          <el-input v-model="metaForm.contractNum" disabled/>
        </el-form-item>
        <el-form-item label="档案序号" prop="archiveNo">
          <el-input v-model.trim="metaForm.archiveNo" maxlength="64" clearable placeholder="请输入财务档案序号"/>
        </el-form-item>
        <el-form-item label="是否存档" prop="archivedFlag">
          <el-radio-group v-model="metaForm.archivedFlag">
            <el-radio :label="1">已存档</el-radio>
            <el-radio :label="0">未存档</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="产品大类修正" prop="productCategoryOverride">
          <el-select
            v-model="metaForm.productCategoryOverride"
            clearable
            placeholder="不修正，按合同构成自动归类"
            style="width: 100%"
          >
            <el-option
              v-for="category in dashboard.productCategoryOptions"
              :key="category"
              :label="category"
              :value="category"
            />
          </el-select>
          <div class="form-help">历史合同无构成或自动归类不准确时，由财务在此修正。</div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="metaDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="metaSaving" @click="saveMeta">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryReceivableLedger, saveReceivableLedgerMeta } from '@/api/crm/finance'
import download from '@/plugins/download'

const emptyDashboard = () => ({
  summary: {
    contractAmount: 0,
    fundAmount: 0,
    invoiceAmount: 0,
    outstandingAmount: 0,
    uninvoicedAmount: 0,
    signedInvoiceAmount: 0,
    signedContractCount: 0,
    contractCount: 0,
    mixedCategoryContractCount: 0,
    fallbackCategoryContractCount: 0
  },
  ledgerRows: [],
  ledgerTotal: 0,
  contractMonthlyRows: [],
  fundMonthlyRows: [],
  invoiceMonthlyRows: [],
  businessInvoiceMonthlyRows: [],
  invoiceCumulativeRows: [],
  personalFundRows: [],
  outstandingDepartmentRows: [],
  contractYearOptions: [],
  departmentOptions: [],
  productCategoryOptions: []
})

export default {
  name: 'ReceivableLedger',
  data () {
    return {
      loading: false,
      exporting: false,
      reportYear: String(new Date().getFullYear()),
      queryParams: {
        contractYear: null,
        departmentName: null,
        productCategory: null,
        keyword: ''
      },
      dashboard: emptyDashboard(),
      pageNum: 1,
      pageSize: 20,
      metaDialogVisible: false,
      metaSaving: false,
      metaForm: {
        contractId: null,
        contractNum: '',
        archiveNo: '',
        archivedFlag: 0,
        productCategoryOverride: null
      },
      metaRules: {
        archiveNo: [{ max: 64, message: '档案序号不能超过64个字符', trigger: 'blur' }],
        archivedFlag: [{ required: true, message: '请选择是否存档', trigger: 'change' }]
      },
      months: Array.from({ length: 12 }, (item, index) => ({
        value: index + 1,
        label: `${index + 1}月`
      }))
    }
  },
  computed: {
    pagedLedgerRows () {
      return this.dashboard.ledgerRows
    }
  },
  created () {
    this.queryData()
  },
  methods: {
    amount (value) {
      return Number(value || 0).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    buildParams () {
      return {
        reportYear: Number(this.reportYear),
        scene: 'ledger',
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        contractYear: this.queryParams.contractYear || undefined,
        departmentName: this.queryParams.departmentName || undefined,
        productCategory: this.queryParams.productCategory || undefined,
        keyword: this.queryParams.keyword || undefined
      }
    },
    queryData () {
      if (!this.reportYear) return
      this.loading = true
      queryReceivableLedger(this.buildParams()).then(response => {
        this.dashboard = Object.assign(emptyDashboard(), response.data || {})
      }).finally(() => {
        this.loading = false
      })
    },
    handleQuery () {
      this.pageNum = 1
      this.queryData()
    },
    resetQuery () {
      this.reportYear = String(new Date().getFullYear())
      this.queryParams = {
        contractYear: null,
        departmentName: null,
        productCategory: null,
        keyword: ''
      }
      this.pageNum = 1
      this.queryData()
    },
    openMetaDialog (row) {
      this.metaForm = {
        contractId: row.contractId,
        contractNum: row.contractNum,
        archiveNo: row.archiveNo || '',
        archivedFlag: Number(row.archivedFlag || 0),
        productCategoryOverride: row.productCategoryOverride || null
      }
      this.metaDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.metaForm) this.$refs.metaForm.clearValidate()
      })
    },
    saveMeta () {
      this.$refs.metaForm.validate(valid => {
        if (!valid) return
        this.metaSaving = true
        saveReceivableLedgerMeta({
          contractId: this.metaForm.contractId,
          archiveNo: this.metaForm.archiveNo || null,
          archivedFlag: this.metaForm.archivedFlag,
          productCategoryOverride: this.metaForm.productCategoryOverride || null
        }).then(() => {
          this.$message.success('台账维护信息已保存')
          this.metaDialogVisible = false
          this.queryData()
        }).finally(() => {
          this.metaSaving = false
        })
      })
    },
    handleExport () {
      this.exporting = true
      download.post('/crmContractFinance/receivableLedger/export', this.buildParams())
      window.setTimeout(() => {
        this.exporting = false
      }, 1000)
    }
  }
}
</script>

<style lang="scss" scoped>
.receivable-ledger-page {
  min-height: calc(100vh - 84px);
  background: #f5f7fa;
}

.page-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.page-title {
  color: #1f2d3d;
  font-size: 21px;
  font-weight: 600;
}

.page-desc,
.table-tip {
  margin-top: 8px;
  color: #8492a6;
  font-size: 13px;
  line-height: 1.6;
}

.query-form {
  margin-top: 20px;
}

.query-form ::v-deep .el-form-item {
  margin-bottom: 4px;
}

.quality-alert,
.content-card {
  margin-bottom: 14px;
}

.content-card {
  min-height: 680px;
}

.table-tip {
  margin: 0 0 12px;
}

.section-title {
  margin: 4px 0 12px;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.section-gap {
  margin-top: 24px;
}

.form-help {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .page-heading {
    flex-direction: column;
  }
}
</style>
